import { FC, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";

import { RiInformationLine } from "react-icons/ri";

import { ClaimLink } from "../../../../../types/claims";
import { apiService } from "../../../../../services/apiService";
import useNotifier from "../../../../../hooks/useNotifier";
import CoreTextarea from "../../../../ui/core/CoreTextarea";
import {
  EditErrorAlert,
  FieldMessage,
} from "../record-payment/ClaimEditDialogParts";
import { claimTag, describeLink, parseClaimLinkError } from "./claimLinks";

interface UnlinkClaimDialogProps {
  open: boolean;
  /** The claim the dialog was opened from (either side). */
  claimId: string;
  link: ClaimLink;
  onClose: () => void;
  onUnlinked: () => void;
}

/** Admin-only: removes a link, from either claim, with a required reason (REV-2629). */
const UnlinkClaimDialog: FC<UnlinkClaimDialogProps> = ({
  open,
  claimId,
  link,
  onClose,
  onUnlinked,
}) => {
  const theme = useTheme();
  const notifier = useNotifier();
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setReasonError(undefined);
    setError(null);
    setIsUnlinking(false);
  }, [open]);

  const handleUnlink = async () => {
    if (!reason.trim()) {
      setReasonError("Enter why the claims are being unlinked.");
      return;
    }
    setIsUnlinking(true);
    setError(null);
    try {
      const response = await apiService.unlinkClaim(
        claimId,
        link.id,
        reason.trim(),
      );
      if (response.success) {
        notifier.showSuccess("Claims unlinked.");
        onUnlinked();
        onClose();
        return;
      }
      const refusal = parseClaimLinkError(response);
      if (refusal.fieldErrors.reason) {
        setReasonError(refusal.fieldErrors.reason);
        return;
      }
      if (refusal.code === "not_found") {
        // Someone else already unlinked them: show the claim as it is now
        notifier.showError("These claims are no longer linked.");
        onUnlinked();
        onClose();
        return;
      }
      setError(refusal.message);
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleClose = () => {
    if (isUnlinking) return;
    onClose();
  };

  const original = claimTag(link.parentClaimNumber);
  const corrected = claimTag(link.childClaimNumber);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontSize: "19px",
        }}
      >
        Unlink claims
        <Typography
          component="div"
          sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
        >
          {describeLink(link.parentClaimNumber, link.childClaimNumber)}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <Alert
            severity="warning"
            icon={<RiInformationLine size={20} />}
            sx={{ maxWidth: "100%" }}
          >
            {original}'s timeline stops showing {corrected}'s remits, and they
            stop counting toward its success fee. Both claims' logs keep a
            record of the link and of why it was removed.
          </Alert>
          <Box>
            <CoreTextarea
              title="Reason"
              placeholder="Why are these claims being unlinked?"
              value={reason}
              minRows={3}
              maxRows={6}
              disabled={isUnlinking}
              onChange={(e) => {
                setReason(e.target.value);
                if (e.target.value.trim()) setReasonError(undefined);
              }}
              aria-label="Reason"
            />
            <FieldMessage error={reasonError}>Required.</FieldMessage>
          </Box>
          {error && <EditErrorAlert message={error} />}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button onClick={handleClose} variant="outlined" disabled={isUnlinking}>
          Cancel
        </Button>
        <Button
          onClick={handleUnlink}
          variant="contained"
          color="error"
          loading={isUnlinking}
        >
          Unlink
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnlinkClaimDialog;
